import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ArchivedAccount {
  id: string;
  name: string;
  type: string;
  institution: string | null;
  color: string;
  archived: boolean;
  is_active: boolean;
  balance: number;
  credit_limit: number | null;
}

export function useArchivedAccounts() {
  const [accounts, setAccounts] = useState<ArchivedAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('accounts')
        .select('id, name, type, institution, color, archived, is_active, balance, credit_limit')
        .eq('user_id', user.id)
        .order('name');

      if (error) throw error;
      setAccounts((data || []) as ArchivedAccount[]);
    } catch (error: any) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const toggleArchive = async (accountId: string, currentArchived: boolean) => {
    try {
      const { error } = await supabase
        .from('accounts')
        .update({ archived: !currentArchived })
        .eq('id', accountId);

      if (error) throw error;

      toast({
        title: !currentArchived ? 'Conta arquivada' : 'Conta desarquivada',
        description: !currentArchived
          ? 'A conta não aparecerá mais nos filtros e dashboard.'
          : 'A conta voltará a aparecer normalmente.',
      });

      await fetchAccounts();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao arquivar conta',
        description: error.message,
      });
    }
  };

  const deleteAccount = async (accountId: string) => {
    try {
      const { error } = await supabase
        .from('accounts')
        .delete()
        .eq('id', accountId);

      if (error) throw error;

      toast({
        title: 'Conta excluída',
        description: 'A conta foi removida permanentemente.',
      });

      await fetchAccounts();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao excluir conta',
        description: error.message,
      });
    }
  };

  return { accounts, loading, refetch: fetchAccounts, toggleArchive, deleteAccount };
}
